import { AuthApiAdapter } from '@modules/Auth/adapters/AuthApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { CancelledError, type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import globalCache from '@shared/lib/GlobalCache';
import { useAuthStore } from '../stores/authStore';

const USER_QUERY_KEY = ['me'];
// Applying the authoritative profile must not cancel its own /me request in another observer.
const applyingProfiles = new WeakSet<QueryClient>();
const adapter = new AuthApiAdapter(createHttpClient(apiClientWH));
const businessQuery = (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== 'me';

async function clearBusinessData(queryClient: QueryClient) {
  globalCache.clear();
  await queryClient.cancelQueries({ predicate: businessQuery });
  queryClient.removeQueries({ predicate: businessQuery });
}

interface UseUserDataOptions {
  enabled?: boolean;
  preserveLegacyImpersonation?: boolean;
  revalidateOnMount?: boolean | 'always';
  staleTime?: number;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean | 'always';
}
export const useUserData = (options: UseUserDataOptions = {}) => {
  const { token, authorizationNeedsRefresh } = useAuthStore();
  const queryClient = useQueryClient();
  const {
    enabled = true,
    preserveLegacyImpersonation = false,
    revalidateOnMount = 'always',
    refetchInterval = false,
    refetchOnWindowFocus = 'always',
    staleTime = 1000 * 60 * 5,
  } = options;

  const query = useQuery({
    queryKey: USER_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const session = useAuthStore.getState();
      const response = await adapter.getCurrentUser(signal);
      const isCurrent = () => {
        const current = useAuthStore.getState();
        return !signal.aborted && current.token === session.token && current.team?.id === session.team?.id;
      };
      if (!isCurrent()) throw new CancelledError({ revert: true, silent: true });
      if (preserveLegacyImpersonation && session.isImpersonating && !response.authorization) {
        useAuthStore.getState().setSettings(response.settings ?? null);
        return response;
      }
      const changed =
        session.user?.id !== response.user.id ||
        session.team?.id !== response.team?.id ||
        session.role?.id !== response.role?.id ||
        session.role?.permissions_version !== response.role?.permissions_version ||
        JSON.stringify(session.authorization) !== JSON.stringify(response.authorization ?? null);
      if (changed) await clearBusinessData(queryClient);
      if (!isCurrent()) throw new CancelledError({ revert: true, silent: true });
      applyingProfiles.add(queryClient);
      try {
        useAuthStore.getState().applyCurrentUser(response);
      } finally {
        applyingProfiles.delete(queryClient);
      }
      return response;
    },
    enabled: enabled && !!token,
    staleTime,
    refetchOnMount: revalidateOnMount,
    refetchOnWindowFocus,
    refetchInterval,
    refetchIntervalInBackground: false,
    retry: false,
  });

  useEffect(
    () =>
      useAuthStore.subscribe((state, previous) => {
        if (
          !applyingProfiles.has(queryClient) &&
          (state.token !== previous.token || state.team?.id !== previous.team?.id)
        ) {
          void clearBusinessData(queryClient);
          void queryClient.cancelQueries({ queryKey: USER_QUERY_KEY, exact: true });
          queryClient.removeQueries({ queryKey: USER_QUERY_KEY, exact: true });
        }
      }),
    [queryClient],
  );

  useEffect(() => {
    if (authorizationNeedsRefresh && token) {
      // Observers share one recovery. Business data is cleared only if /me confirms a change.
      void queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY }, { cancelRefetch: false });
    }
  }, [authorizationNeedsRefresh, token, queryClient]);

  const refreshUserData = () => queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
  return { ...query, refreshUserData };
};
